// @givmo/app-admin — app shell (SaaS redesign 2026-06-26; nav regrouped EP-DES2 Wave 0).
//
// A two-pane fintech-SaaS shell (Stripe/Mercury/Ramp/Linear caliber) replacing the Flutter-ported
// `MenuTemplate`/`CMenu` nav. The DATA CONTRACT: the always-on nav destinations in order with stable
// `data-testid`s that Playwright relies on (`admin-app-shell`, `admin-sidebar`, `admin-brand`,
// `admin-content`, `menu-dashboard`…`menu-support`, `menu-terms`, `menu-privacy`, `menu-logout`),
// and the `useAuth().signOut` wiring. (`menu-reporting` was retired in 07-3b gap ③; `menu-documents`
// became `menu-reports` in Wave 0 when /documents was absorbed into /reports. Both old routes
// redirect, so no bookmark 404s — but the two testIds are gone and e2e retargets to `menu-reports`.)
// Wave 1 added `menu-approvals` when the match queue got its own destination.
//
// Wave 0 regrouped the sidebar around the four JOBS an admin does (spec §1) — Overview · Giving ·
// People · Account — instead of the six nouns the Flutter app happened to have. No route moved and
// no testId changed: only `group`, three labels, and the promotion of `/give` (dark-gated) from a
// dashboard button to a real destination. The shell also carries the reserved assistant slot (§7).
//
// Layout:
//   - SIDEBAR (256px, `surface`, `border-r border-subtle`): brand lockup → grouped nav (icon 18px
//     + 14px/medium label, `rounded-md`, hover `surface-hover`, active = `surface-active` bg +
//     `brand-700` text + a subtle left accent rail — NOT a full solid-blue bar) → a bottom account
//     row (avatar + name + role + sign-out) with quiet Terms/Privacy links.
//   - TOP BAR (sticky, 56px, `surface`, `border-b border-subtle`): the EP-SUITE3 company-suite
//     waffle (top-left, no DOM while `VITE_COMPANY_CONSOLE_ENABLED` is off) → the breadcrumb → a help
//     icon + the account menu.
//   - CONTENT: `bg-surface-sunken`, the screen rendered inside.

import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyDashboardOptions,
} from '@givmo/api-client/query';
import {
  Avatar,
  cn,
  FOCUS_RING,
  GivmoLogomark,
  GivmoWordmark,
  Icon,
  IconButton,
  Menu,
  Skeleton,
  testIdProp,
  toast,
  iconButtonVariants,
  NAV_DRAWER_TOUCH_TARGET,
  navDrawerClasses,
  useNavDrawer,
} from '@givmo/design-system';
import {
  LayoutDashboard,
  ClipboardCheck,
  Settings,
  Users,
  FileText,
  UserCog,
  HelpCircle,
  HeartHandshake,
  HandHeart,
  Coins,
  Gift,
  Footprints,
  KeyRound,
  LogOut,
  LifeBuoy,
  ChevronRight,
  Menu as MenuGlyph,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import type { SVGProps } from 'react';
import { toApprovalRows } from '@givmo/employer-module/features/dashboard/dashboard-decode';
import { passkeysUsableHere, useMyPasskeysOnDemand } from '@givmo/passkeys';
import { useAuth } from '../auth/auth-context';
import { SIGN_IN_SECURITY_ROUTE } from '../features/sign-in-security/sign-in-security-page';
import { TERMS_URL, PRIVACY_URL } from '../lib/constants';
import {
  isCompanyDirectGivingEnabled,
  isCompanyFundraisersEnabled,
  isCompanyGiftCardsEnabled,
  isCompanyStepsCompetitionsEnabled,
} from '../lib/env';
import { AssistantEntryPointSlot } from './assistant-slot';
import { AppSwitcherSlot } from './app-switcher-slot';

type LucideIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

/**
 * The four JOBS an employer admin does — approve, give, run the people program, prove it — which is
 * what the sidebar is now organized around (EP-DES2 Wave 0; spec §1). The previous labels
 * (`Overview / Manage / Account`) described nothing; these make the sidebar the product's table of
 * contents, and in particular make it visible that the company gives its OWN money rather than only
 * matching its employees'.
 *
 * Approvals joined Overview in Wave 1 when the queue split out of the dashboard (EP-DES1 §1). Match
 * Program joined Giving at EP-MNT2 as the group's first ALWAYS-ON row: the match policy left Settings
 * for a page of its own, so Giving now renders in every build, ahead of its switched rows.
 * Campaigns (Giving) is still reserved and deliberately NOT registered here; Competitions (People) is
 * a dark overlay below, not an always-on entry.
 */
type NavGroup = 'Overview' | 'Giving' | 'People' | 'Account';

interface NavItem {
  to: string;
  label: string;
  testId: string;
  icon: LucideIcon;
  /** Section the item belongs to (drives the grouped layout). */
  group: NavGroup;
  /** Render the pending-approval count as a trailing badge on this entry (spec §115). */
  approvalBadge?: boolean;
}

/**
 * The ALWAYS-ON sidebar destinations, in display order. The route paths + `menu-*` testIds are the
 * data contract (`givmo-e2e-test` selects on them); labels and `group` are presentational.
 *
 * Every dark surface is deliberately ABSENT from this array — see the overlay items below. That is
 * the invariant the flag-off tests pin: with every VITE switch off, the sidebar is exactly these
 * eight, and no dark destination has an entry point.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    testId: 'menu-dashboard',
    icon: LayoutDashboard,
    group: 'Overview',
  },
  {
    to: '/approvals',
    label: 'Approvals',
    testId: 'menu-approvals',
    icon: ClipboardCheck,
    group: 'Overview',
    approvalBadge: true,
  },
  {
    to: '/match-program',
    label: 'Match Program',
    testId: 'menu-match-program',
    icon: Coins,
    group: 'Giving',
  },
  {
    to: '/employees',
    label: 'Employees',
    testId: 'menu-employees',
    icon: Users,
    group: 'People',
  },
  { to: '/admins', label: 'Admin Team', testId: 'menu-admins', icon: UserCog, group: 'People' },
  {
    to: '/reports',
    label: 'Reports',
    testId: 'menu-reports',
    icon: FileText,
    group: 'Account',
  },
  { to: '/settings', label: 'Settings', testId: 'menu-settings', icon: Settings, group: 'Account' },
  { to: '/support', label: 'Help', testId: 'menu-support', icon: HelpCircle, group: 'Account' },
];

const NAV_GROUP_ORDER: readonly NavGroup[] = ['Overview', 'Giving', 'People', 'Account'];

/**
 * EP-WEB1: "Give" is PROMOTED to a real nav destination in Wave 0 (spec §1 — direct giving had no
 * sidebar entry at all, reachable only from a dashboard button). It stays DARK-gated exactly as the
 * dashboard button is: the entry appears ONLY when `isCompanyDirectGivingEnabled()`
 * (VITE_COMPANY_DIRECT_GIVING_ENABLED) is on, matching the `/give` route's redirect gate — a visible
 * entry to a route that bounces to /dashboard would be a dead control.
 */
const GIVE_NAV_ITEM: NavItem = {
  to: '/give',
  label: 'Donate',
  testId: 'menu-give',
  icon: HandHeart,
  group: 'Giving',
};

/**
 * EP-WEB3: the Fundraisers destination is DARK by default — it appears in the sidebar (and resolves
 * a top-bar title) ONLY when `isCompanyFundraisersEnabled()` (VITE_COMPANY_FUNDRAISERS_ENABLED)
 * is on. While off there is NO entry point, matching the route's redirect gate.
 */
const COMPANY_FUNDRAISERS_NAV_ITEM: NavItem = {
  to: '/company-fundraisers',
  label: 'Fundraisers',
  testId: 'menu-company-fundraisers',
  icon: HeartHandshake,
  group: 'Giving',
};

/**
 * EP-WEB4: the "Gift Cards" destination is DARK by default — it appears in the sidebar (and resolves
 * a top-bar title) ONLY when `isCompanyGiftCardsEnabled()` (VITE_COMPANY_GIFT_CARDS_ENABLED) is on.
 * While off there is NO entry point, matching the route's redirect gate.
 */
const COMPANY_GIFT_CARDS_NAV_ITEM: NavItem = {
  to: '/gift-cards',
  label: 'Gift Cards',
  testId: 'menu-gift-cards',
  icon: Gift,
  group: 'Giving',
};

/**
 * EP-WEB10: the "Competitions" destination is DARK by default — it appears in the sidebar (and resolves
 * a top-bar title) ONLY when `isCompanyStepsCompetitionsEnabled()`
 * (VITE_COMPANY_STEPS_COMPETITIONS_ENABLED) is on. While off there is NO entry point, matching the
 * routes' redirect gate. It sits in People (spec §1): a steps competition is a program you run FOR your
 * employees, not a way the company gives its own money.
 */
const COMPETITIONS_NAV_ITEM: NavItem = {
  to: '/competitions',
  label: 'Competitions',
  testId: 'menu-competitions',
  icon: Footprints,
  group: 'People',
};

/** The nav items actually shown, with dark-gated destinations added only when their switch is on.
 *  Each gate is INDEPENDENT (give vs. fundraisers vs. gift cards vs. competitions flip separately);
 *  NAV_ITEMS stays untouched, so with every switch off the sidebar is byte-identical to NAV_ITEMS. Push
 *  order fixes the within-group order of the Giving section: the always-on Match Program, then Give →
 *  Fundraisers → Gift Cards (pushed after every NAV_ITEMS row, so after it). People is
 *  Employees → Competitions → Admin Team, which the filter preserves by inserting Competitions between
 *  the two always-on People entries rather than appending it after them. */
function resolveNavItems(): NavItem[] {
  const items = [...NAV_ITEMS];
  if (isCompanyDirectGivingEnabled()) items.push(GIVE_NAV_ITEM);
  if (isCompanyFundraisersEnabled()) items.push(COMPANY_FUNDRAISERS_NAV_ITEM);
  if (isCompanyGiftCardsEnabled()) items.push(COMPANY_GIFT_CARDS_NAV_ITEM);
  if (isCompanyStepsCompetitionsEnabled()) {
    const admins = items.findIndex((i) => i.testId === 'menu-admins');
    items.splice(admins < 0 ? items.length : admins, 0, COMPETITIONS_NAV_ITEM);
  }
  return items;
}

/**
 * Shell pages no sidebar row leads to — an account-menu row does — titled for the bar all the same,
 * so the breadcrumb never names a page the person is not on (AS-WEB11). Each is named only while it
 * has something to show: the Sign-in & security page renders nothing while the passkey surface is
 * not served, and then its address reads like any other unknown one.
 */
const UNLISTED_TITLES: Readonly<Record<string, string>> = {
  [SIGN_IN_SECURITY_ROUTE]: 'Sign-in & security',
};

/** Resolve the human-facing title for the current pathname (top bar + a11y). */
function activeTitle(
  pathname: string,
  items: readonly NavItem[],
  unlisted: Readonly<Record<string, string>>,
): string {
  const match = items.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  return match?.label ?? unlisted[pathname] ?? 'Dashboard';
}

export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { user, signOut } = useAuth();
  // AS-WEB11: the account menu's "Sign-in & security" row renders only where its page has something
  // to show — the passkey surface answering here (the card's own dark gate, one shared read, kept
  // through a failed re-read and withdrawn only by the dark 404).
  // AS-WEB16: read as the person reaches for the menu (the pointer on its trigger, the trigger's own
  // focus, the menu opening), never on a page load or the window's return — that was a request per
  // page, and a 404 per page wherever passkeys are dark. On the Sign-in & security page the page's own
  // read lands in the same cache, so the bar still names it there.
  // EP-MNT18: and only on a page host a passkey can work on (`passkeysUsableHere`: `givmo.io` or a
  // subdomain): on a raw `*.web.app` host nothing is read, so neither the row nor the page shows.
  const { passkeys: menuPasskeys, read: readMenuPasskeys } = useMyPasskeysOnDemand(
    passkeysUsableHere() ? (user?.uid ?? null) : null,
  );
  const passkeysServed = menuPasskeys !== undefined;
  const { open, toggle, close, drawerRef, triggerRef } = useNavDrawer();
  const roleQuery = useQuery(companiesGetCompanyAdminOptions());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Destination clicks also cover the current route; this covers history and
  // programmatic navigation while the same authenticated shell stays mounted.
  useEffect(close, [pathname, close]);
  // The top bar wraps to a second row on a phone, so its real height, not 56 px, is what a toast sits under
  // (the design system's Toaster reads `--app-top-bar-h`; FCM-WEB35's production fix 3, FCM-WEB75).
  const topBarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const bar = topBarRef.current;
    if (bar === null) return;
    const root = document.documentElement;
    // Unmeasured (no layout yet), the Toaster's own 56 px stands.
    const publish = () =>
      bar.offsetHeight > 0
        ? root.style.setProperty('--app-top-bar-h', `${bar.offsetHeight}px`)
        : root.style.removeProperty('--app-top-bar-h');
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--app-top-bar-h');
    };
  }, []);
  // The Approvals badge count. Rides the SAME query key the dashboard and `/approvals` use, so on
  // those screens it is a cache read; elsewhere it costs one GET per staleTime window, which is the
  // price of a nav badge that is correct on every screen rather than only on the two that fetch it.
  // A pending/failed read yields 0 → no badge, never a wrong number.
  const approvalsRead = useQuery(companiesGetCompanyDashboardOptions());
  const pendingApprovals = useMemo(
    () => toApprovalRows(approvalsRead.data).filter((r) => r.status === 'Pending').length,
    [approvalsRead.data],
  );
  const navItems = resolveNavItems();
  const title = activeTitle(pathname, navItems, passkeysServed ? UNLISTED_TITLES : {});
  const [isSigningOut, setIsSigningOut] = useState(false);
  const signOutPendingRef = useRef(false);

  const accountName = user?.displayName?.trim() || null;
  const accountEmail = user?.email?.trim() || null;
  const accountPrimary = accountName ?? accountEmail;
  const accountRole = roleQuery.data?.admin_company_role?.trim() || null;
  const accountReady = user !== null && !roleQuery.isPending && accountPrimary !== null;

  const handleSignOut = useCallback(async () => {
    if (signOutPendingRef.current) return;
    signOutPendingRef.current = true;
    setIsSigningOut(true);
    try {
      await signOut();
      queryClient.clear();
      await navigate({ to: '/login-home', replace: true });
      // Admin's mounted shell queries can be recreated between the first clear and route teardown.
      // Clear once more after navigation so the signed-out landing has no company-scoped cache.
      queryClient.clear();
    } catch {
      toast.error('We couldn’t sign you out. Please try again.');
      signOutPendingRef.current = false;
      setIsSigningOut(false);
    }
  }, [navigate, queryClient, signOut]);

  function isActive(to: string): boolean {
    return pathname === to || pathname.startsWith(`${to}/`);
  }

  return (
    <div className="flex min-h-screen bg-surface-sunken" {...testIdProp('admin-app-shell')}>
      {/* ── Sidebar ─────────────────────────────────────────────────────────────────────────── */}
      <div
        ref={drawerRef}
        id="admin-sidebar"
        className={navDrawerClasses(open)}
        role={open ? 'dialog' : 'navigation'}
        aria-modal={open ? true : undefined}
        aria-label={open ? 'Navigation' : 'Primary'}
        {...testIdProp('admin-sidebar')}
      >
        {/* Brand lockup */}
        <div className="flex h-[56px] items-center justify-between px-20">
          <Link
            to="/dashboard"
            onClick={close}
            className="flex min-h-44 items-center gap-8 outline-none focus-visible:shadow-ring rounded-md lg:min-h-0"
            {...testIdProp('admin-brand')}
          >
            <GivmoWordmark className="h-[22px] w-auto text-brand-500" />
          </Link>
          <button
            type="button"
            onClick={close}
            aria-label="Close navigation"
            className={`-mr-12 ${NAV_DRAWER_TOUCH_TARGET}`}
            {...testIdProp('admin-nav-close')}
          >
            <Icon icon={X} size={20} aria-hidden />
          </button>
        </div>

        {/* Grouped nav */}
        <div className="flex-1 overflow-y-auto px-12 py-12">
          {NAV_GROUP_ORDER.map((group) => {
            const items = navItems.filter((i) => i.group === group);
            if (items.length === 0) return null;
            return (
              <div key={group} className="mb-16 last:mb-0">
                <p className="mb-4 px-12 font-sans text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
                  {group}
                </p>
                {/* One active-state recipe for EVERY item (the shell is shared, so this is the single
                    source of truth): inset, `rounded-md` row; active = `surface-active` bg +
                    `brand-700` text + a 3px brand accent rail INSET within the row (not flush to the
                    panel edge). Idle = secondary text, `surface-hover` on hover. */}
                <ul className="flex flex-col gap-2">
                  {items.map((item) => {
                    const active = isActive(item.to);
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          onClick={close}
                          className={[
                            'group relative flex h-44 items-center gap-10 rounded-md pl-14 pr-12 font-sans text-s14 tracking-givmo outline-none transition-colors lg:h-36',
                            'focus-visible:shadow-ring',
                            active
                              ? 'bg-surface-active font-w600 text-brand-700'
                              : 'font-w500 text-text-secondary hover:bg-surface-hover hover:text-text-strong',
                          ].join(' ')}
                          aria-current={active ? 'page' : undefined}
                          {...testIdProp(item.testId)}
                        >
                          {/* Brand accent rail — inset 4px from the row's left edge, not flush. */}
                          {active && (
                            <span
                              aria-hidden
                              className="absolute left-[5px] top-1/2 h-16 w-[3px] -translate-y-1/2 rounded-full bg-brand-600"
                            />
                          )}
                          <Icon
                            icon={item.icon}
                            size={18}
                            color="currentColor"
                            className={active ? 'text-brand-600' : 'text-icon'}
                            aria-hidden
                          />
                          <span className="truncate">{item.label}</span>
                          {/* Pending-count badge (spec §115). Data-driven: absent at zero, so the
                              sidebar is quiet when there is nothing waiting. The count is in the
                              accessible name too — a bare number next to "Approvals" reads as
                              nonsense to a screen reader. */}
                          {item.approvalBadge === true && pendingApprovals > 0 && (
                            <span
                              className="ml-auto inline-flex h-18 min-w-18 shrink-0 items-center justify-center rounded-full bg-warning-100 px-6 font-sans text-s11 font-w600 text-warning-700 tabular-nums"
                              aria-label={`${pendingApprovals} waiting`}
                              {...testIdProp('menu-approvals-badge')}
                            >
                              {pendingApprovals}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom account region — the sign-out is an always-present control (`menu-logout`) so the
            e2e/contract selector stays directly clickable, with the user identity inline. */}
        <div className="border-t border-border-subtle p-12">
          <div className="flex items-center gap-10 rounded-md p-8">
            {accountReady ? (
              <>
                <Avatar name={accountPrimary} src={user.photoURL ?? undefined} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                    {accountPrimary}
                  </span>
                  {accountRole ? (
                    <span className="block truncate font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {accountRole}
                    </span>
                  ) : null}
                </span>
              </>
            ) : (
              <div
                className="flex min-w-0 flex-1 items-center gap-10"
                {...testIdProp('sidebar-account-skeleton')}
              >
                <Skeleton className="size-32 shrink-0 rounded-full" />
                <span className="flex min-w-0 flex-1 flex-col gap-6">
                  <Skeleton className="h-12 w-3/4" />
                  <Skeleton className="h-10 w-1/2" />
                </span>
              </div>
            )}
            <IconButton
              aria-label="Sign out"
              tone="ghost"
              disabled={isSigningOut}
              aria-busy={isSigningOut || undefined}
              className="size-44 lg:size-36"
              onClick={() => void handleSignOut()}
              {...testIdProp('menu-logout')}
            >
              <Icon icon={LogOut} size={18} aria-hidden />
            </IconButton>
          </div>

          <div className="mt-4 flex items-center gap-12 px-8">
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              onClick={close}
              className="inline-flex min-h-44 min-w-44 items-center font-sans text-s12 font-w400 tracking-givmo text-text-tertiary outline-none transition-colors hover:text-text-secondary focus-visible:text-text-secondary lg:min-h-0 lg:min-w-0"
              {...testIdProp('menu-terms')}
            >
              Terms
            </a>
            <span aria-hidden className="text-text-placeholder">
              ·
            </span>
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              onClick={close}
              className="inline-flex min-h-44 min-w-44 items-center font-sans text-s12 font-w400 tracking-givmo text-text-tertiary outline-none transition-colors hover:text-text-secondary focus-visible:text-text-secondary lg:min-h-0 lg:min-w-0"
              {...testIdProp('menu-privacy')}
            >
              Privacy
            </a>
          </div>
        </div>
      </div>

      {/* ── Main column ─────────────────────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col" inert={open}>
        {/* Top bar — a breadcrumb (NOT the page title; the PageHeader owns the title, so the same
            string is never echoed twice in the first 100px). */}
        <header
          ref={topBarRef}
          className="sticky top-0 z-10 flex min-h-[56px] shrink-0 flex-wrap items-center justify-between gap-x-16 gap-y-0 border-b border-border-subtle bg-surface px-16 py-6 lg:h-[56px] lg:flex-nowrap lg:px-24 lg:py-0"
        >
          {/* EP-SUITE3: the company-suite waffle, FIRST in the top bar. It renders no DOM while
              `VITE_COMPANY_CONSOLE_ENABLED` is off, so the flag-off bar is byte-identical to before —
              and it lives here rather than in the sidebar because the sidebar is navigation WITHIN
              this portal (and carries a byte-identity pin), while this moves between APPS. */}
          <div className="flex min-w-0 flex-1 basis-[160px] items-center gap-12 lg:flex-none lg:basis-auto [&_button]:min-h-44 [&_button]:min-w-44 lg:[&_button]:min-h-0 lg:[&_button]:min-w-0">
            <button
              ref={triggerRef}
              type="button"
              onClick={toggle}
              aria-label={open ? 'Close navigation' : 'Open navigation'}
              aria-expanded={open}
              aria-controls="admin-sidebar"
              className={`-ml-12 ${NAV_DRAWER_TOUCH_TARGET}`}
              {...testIdProp('admin-nav-toggle')}
            >
              <Icon icon={MenuGlyph} size={20} aria-hidden />
            </button>
            <AppSwitcherSlot />
            <nav
              aria-label="Breadcrumb"
              className="flex min-w-0 items-center gap-6 font-sans text-s13 tracking-normal"
            >
              <Link
                to="/dashboard"
                aria-label="Givmo home"
                className={cn(
                  'flex min-h-44 min-w-44 shrink-0 items-center justify-center rounded-md outline-none lg:hidden',
                  FOCUS_RING,
                )}
                {...testIdProp('admin-brand-compact')}
              >
                <GivmoLogomark className="h-[22px] w-auto text-brand-500" />
              </Link>
              <Link
                to="/dashboard"
                className="hidden font-w500 text-text-tertiary outline-none transition-colors hover:text-text-secondary focus-visible:text-text-secondary lg:inline"
              >
                Home
              </Link>
              <Icon
                icon={ChevronRight}
                size={14}
                className="shrink-0 text-icon-muted"
                aria-hidden
              />
              <span className="truncate font-w600 text-text-secondary" aria-current="page">
                {title}
              </span>
            </nav>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-8 [&_button]:min-h-44 [&_button]:min-w-44 lg:[&_button]:min-h-0 lg:[&_button]:min-w-0">
            {/* EP-DES2 Wave 0: the reserved assistant entry point (spec §7). Renders NOTHING until
                EP-AI1 registers into it — the slot exists so the frozen shell never has to reopen. */}
            <AssistantEntryPointSlot />
            {/* The help control resolves to the IN-APP `/support` page — the same destination as the
                sidebar's Help entry, so the two never diverge. It previously linked out to
                `https://www.givmo.io/support`, which 404s (EP-FIX6). EP-DES1 rebuilds this Help
                surface; until then it must stay a real, reachable destination. */}
            <Link
              to="/support"
              aria-label="Help and support"
              className={cn(
                iconButtonVariants({ tone: 'ghost', size: 'md' }),
                'size-44 lg:size-36',
              )}
              {...testIdProp('menu-help')}
            >
              <Icon icon={LifeBuoy} size={18} aria-hidden />
            </Link>
            {accountReady ? (
              <Menu.Root
                onOpenChange={(opening) => {
                  if (opening) void readMenuPasskeys();
                }}
              >
                <Menu.Trigger
                  className="flex size-44 items-center justify-center rounded-full outline-none focus-visible:shadow-ring lg:size-auto"
                  aria-label="Account menu"
                  onPointerEnter={() => void readMenuPasskeys()}
                  onFocus={() => void readMenuPasskeys()}
                >
                  <Avatar name={accountPrimary} src={user.photoURL ?? undefined} size="sm" />
                </Menu.Trigger>
                <Menu.Content side="bottom" align="end" className="min-w-[212px]">
                  <div className="px-16 py-8">
                    {accountName ? (
                      <p className="truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                        {accountName}
                      </p>
                    ) : null}
                    {accountEmail ? (
                      <p
                        className={`truncate font-sans tracking-givmo ${
                          accountName
                            ? 'text-s12 font-w400 text-text-tertiary'
                            : 'text-s13 font-w600 text-text-strong'
                        }`}
                      >
                        {accountEmail}
                      </p>
                    ) : null}
                  </div>
                  <Menu.Separator />
                  {passkeysServed && (
                    <Menu.Item
                      testId="account-menu-sign-in-security"
                      onClick={() => void navigate({ to: SIGN_IN_SECURITY_ROUTE })}
                      startIcon={
                        <Icon icon={KeyRound} size={16} className="text-icon" aria-hidden />
                      }
                    >
                      Sign-in & security
                    </Menu.Item>
                  )}
                  <Menu.Item
                    testId="account-menu-logout"
                    disabled={isSigningOut}
                    onClick={() => void handleSignOut()}
                    startIcon={<Icon icon={LogOut} size={16} className="text-icon" aria-hidden />}
                  >
                    {isSigningOut ? 'Signing out…' : 'Sign out'}
                  </Menu.Item>
                </Menu.Content>
              </Menu.Root>
            ) : (
              <Skeleton
                className="size-32 rounded-full"
                testId="header-account-skeleton"
                aria-label="Loading account"
              />
            )}
          </div>
        </header>

        <main className="min-w-0 flex-1" {...testIdProp('admin-content')}>
          {children}
        </main>
      </div>
      {open && (
        <button
          type="button"
          tabIndex={-1}
          onClick={close}
          onMouseDown={(event) => event.preventDefault()}
          aria-label="Dismiss navigation"
          className="fixed inset-0 z-40 bg-scrim lg:hidden"
          {...testIdProp('admin-nav-backdrop')}
        />
      )}
    </div>
  );
}
